/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useState } from 'react';
import { CircularProgress, createTheme, Tooltip } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import { makeStyles } from '@material-ui/styles';
import { DetailsLink } from '../../components/LazyList/DetailsLink';
import LazyList from '../../components/LazyList/LazyList';
import {
  defaultOnDownload,
  defaultOnSearchChange,
} from '../../components/LazyList/LazyListHelpers';
import { getUserWithBusinessPhoneReport } from '../../services/fetchServices/UserApi/getUserWithBusinessPhoneReport';
import UserListHeader from './UserListHeader';
import {
  ColumnName,
  composeColumnsDefinition,
  mapUserToDataRow,
  NUMBER_OF_USERS_FETCHED,
} from './userListHelpers';
import useUserListData from './useUserListData';

const useStyles = makeStyles({
  button: {
    position: 'relative',
    top: '10px',
    right: '112px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: ' background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    color: 'rgba(0, 0, 0, 0.54)',
    padding: '10px',
    borderRadius: '50%',
    '&:hover': {
      color: '#ffd242',
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    '&:focus': {
      outline: '0',
    },
  },
  spinner: {
    marginRight: '10px',
  },
});

const materialTheme = () =>
  createTheme({
    palette: {
      primary: { main: '#ffd242' },
    },
    typography: {
      fontSize: 18,
      fontFamily: ['ModeratJIT', 'sans-serif'].join(','),
    },
    overrides: {
      MuiPaper: {
        elevation2: {
          width: '30%',
          minWidth: '30%',
          maxWidth: '30%',
        },
      },
      ...(window.location.pathname === '/' && {
        MUIDataTableToolbar: {
          actions: {
            '& > span': {
              position: 'relative',
              right: '-50px',
            },
          },
        },
      }),
    },
  });

const UserList = () => {
  const [isFetchingReport, setIsFetchingReport] = useState(false);

  const {
    state: {
      totalUsers,
      filteredUsers,
      loadedUsers,
      activeFilters,
      availableFilters,
      activeSort,
      searchText,
      isFetching,
      displayedColumns,
    },
    setState,
    onColumnsChangeCallback,
    onDownload,
    contextEnchancedOnFiltersChange,
    contextEnchancedOnSortChange,
  } = useUserListData();
  const onInfiniteScroll = useCallback(
    () =>
      setState((prevState) => ({
        ...prevState,
        numberOfRequestedUsers:
          prevState.numberOfRequestedUsers + NUMBER_OF_USERS_FETCHED,
      })),
    [setState],
  );

  const allActionComponents = (rowData: any[]) => [
    <DetailsLink link={`/user/${rowData[ColumnName.ldapLogin]}`} />,
  ];

  const columns = composeColumnsDefinition(
    activeFilters,
    availableFilters,
    activeSort,
    displayedColumns,
  );

  const handleDownloadUserWithBusinessPhoneReport = () => {
    setIsFetchingReport(true);
    getUserWithBusinessPhoneReport()
      .then(defaultOnDownload('UserWithBusinessPhoneReport.xlsx'))
      .finally(() => setIsFetchingReport(false));
  };

  const classes = useStyles();

  const options = {
    filter: true,
    filterType: 'multiselect',
    responsive: 'standard',
    serverSide: true,
    serverSideFilterList: Object.values(activeFilters),
    pagination: false,
    rowsPerPage: 5000,
    print: false,
    selectableRows: 'none',
    rowHover: false,
    searchText,
    onDownload,
    searchOpen: true,
    textLabels: {
      toolbar: {
        downloadCsv: 'Download Report',
      },
    },
    customToolbar: () => (
      <>
        {window.location.pathname === '/' && (
          <Tooltip title="Download users with business phone report">
            <button
              type="button"
              className={classes.button}
              onClick={handleDownloadUserWithBusinessPhoneReport}
            >
              {!isFetchingReport ? (
                <PhoneIcon />
              ) : (
                <CircularProgress className={classes.spinner} size={20} />
              )}
            </button>
          </Tooltip>
        )}
      </>
    ),
  };

  const data = loadedUsers.map(mapUserToDataRow);

  return (
    <div>
      <UserListHeader />
      <LazyList
        data={data}
        columns={columns}
        options={options}
        onFilterChange={contextEnchancedOnFiltersChange}
        onSortChange={contextEnchancedOnSortChange}
        onSearchChange={defaultOnSearchChange(setState)}
        onColumnsChange={onColumnsChangeCallback}
        totalItems={totalUsers}
        filteredItems={filteredUsers}
        onInfiniteScrollCallback={onInfiniteScroll}
        isFetching={isFetching}
        actionComponents={allActionComponents}
        muiTheme={materialTheme}
      />
    </div>
  );
};

export default UserList;
